import {
  Link as ReactRouterLink,
  type LinkProps,
  type NavigateOptions,
  NavLink as ReactRouterNavLink,
  type NavLinkProps,
  type RelativeRoutingType,
  type To,
  /**
   * Memex uses the exports from @github-ui/react-router because its
   * custom routing doesn't implement RoutesContext making it
   * incompatible with the hooks exported by @github-ui/use-navigate.
   */
  useDangerousNavigate,
  useDangerousSearchParams,
  useLinkClickHandler as useReactRouterLinkClickHandler,
} from '@github-ui/react-router'
import {useCallback} from 'react'

function stateWithDefaults(state?: any) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return {skipTurbo: true, ...state}
}
/**
 * Wraps @github-ui/react-router's useNavigate hook to add a skipTurbo state property
 */
export const useNavigate = () => {
  const navigate = useDangerousNavigate()

  return useCallback(
    (to: To, opts?: NavigateOptions): void | Promise<void> => {
      return navigate(to, {
        ...opts,
        state: stateWithDefaults(opts?.state),
      })
    },
    [navigate],
  )
}

/**
 * Wraps @github-ui/react-router's Link component to add a skipTurbo state property
 */
export function Link({ref, state, ...props}: LinkProps & {ref?: React.Ref<HTMLAnchorElement>}) {
  return <ReactRouterLink data-turbo="false" {...props} state={stateWithDefaults(state)} ref={ref} />
}

/**
 * Wraps @github-ui/react-router's NavLink component to add a skipTurbo state property
 */
export function NavLink({ref, state, ...props}: NavLinkProps & {ref?: React.Ref<HTMLAnchorElement>}) {
  return <ReactRouterNavLink data-turbo="false" {...props} state={stateWithDefaults(state)} ref={ref} />
}

export const useSearchParams = () => {
  const [searchParams, setSearchParams] = useDangerousSearchParams()

  const setUrlSearchParams: typeof setSearchParams = useCallback(
    (nextParams, opts) => {
      setSearchParams(nextParams, {
        ...opts,
        state: stateWithDefaults(opts?.state),
      })
    },
    [setSearchParams],
  )

  return [searchParams, setUrlSearchParams] as const
}

export function useLinkClickHandler<E extends Element = HTMLAnchorElement>(
  to: To,
  opts?: {
    target?: React.HTMLAttributeAnchorTarget
    replace?: boolean
    state?: any
    preventScrollReset?: boolean
    relative?: RelativeRoutingType
  },
): (event: React.MouseEvent<E>) => void {
  const options = {
    ...opts,
    state: stateWithDefaults(opts?.state),
  }
  return useReactRouterLinkClickHandler<E>(to, options)
}
